import { invalidarPctCongelado } from "./comisionSnapshot";
import { congelarPctIntermediacion } from "./congelarPctIntermediacion";
import { getImporteFacturable, TOLERANCIA_MONTO_FACTURABLE } from "./getImporteFacturable";
import { client } from "./supabase";
import { tieneEdificiosAsignados } from "./tieneEdificiosAsignados";

// Guardrail de monto (7.22): compara el neto del DTE contra el importe FACTURABLE de la venta
// (importe × (1 − descuento_acuerdo_pct)). Devuelve null si no hay divergencia relevante o si la
// venta es en USD (el monto_neto del DTE es CLP fiscal, no comparable con un importe en dólares).
// Función pura y exportada → testeable. NO bloquea: el caller decide (aviso blando).
export const avisoDivergenciaMonto = (montoNetoDTE, venta) => {
  if (!venta || venta.moneda_dolar) return null;
  const facturable = getImporteFacturable(venta);
  const dteNeto = Number(montoNetoDTE) || 0;
  const diff = Math.abs(dteNeto - facturable);
  if (diff <= TOLERANCIA_MONTO_FACTURABLE) return null;
  return { dteNeto, facturable, diff };
};

// ─── Facturas SII Emitidas (Ingresos) ────────────────────

export const getFacturasEmitidas = async (pais, filters = {}) => {
  let query = client
    .from("facturas_sii_emitidas")
    .select("*")
    .eq("pais", pais)
    .order("fecha_emision", { ascending: false });

  if (filters.periodo) query = query.eq("periodo_tributario", filters.periodo);
  if (filters.estado) query = query.eq("estado_vinculacion", filters.estado);
  if (filters.rut_receptor) query = query.eq("rut_receptor", filters.rut_receptor);
  if (filters.fechaDesde) query = query.gte("fecha_emision", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha_emision", filters.fechaHasta);
  if (filters.tipo_dte) query = query.eq("tipo_dte", filters.tipo_dte);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getFacturasEmitidasPendientes = async (pais, periodo) =>
  getFacturasEmitidas(pais, { estado: "pendiente", periodo });

export const vincularFacturaEmitida = async (id, vinculacion) => {
  // Guard regla de oro: sin edificios no se vincula. Corre ANTES de cualquier write (1:N +
  // facturas_sii_emitidas + propagación de texto) para que el bloqueo sea atómico y no deje el
  // lado SII a medias. Mismo predicado compartido que la edición manual (useVFEditModal).
  // BTL y negocios_carteleria no aplican: el link no les propaga texto de factura.
  // salesRow captura la fila de sales leída por el guard (cuando venta_tipo === 'sales') para
  // reusarla en el snapshot de monto_usd sin un fetch adicional.
  let salesRow = null;
  let cuotaParaAviso = null;
  if (vinculacion.venta_tipo === "sales_contratos" && vinculacion.venta_ref_index != null) {
    const { data: cGuard } = await client
      .from("sales_contratos")
      .select("meses, descuento_acuerdo_pct, moneda_dolar")
      .eq("id", vinculacion.venta_ref_id)
      .single();
    const cuota = cGuard?.meses?.[vinculacion.venta_ref_index];
    if (!tieneEdificiosAsignados(cuota?.edificios_ids)) {
      const err = new Error(
        `No se puede vincular: la cuota de ${cuota?.mes || "este mes"} no tiene edificios asignados. Asigná los edificios antes de vincular la factura.`
      );
      err.code = "EDIFICIOS_FALTANTES";
      throw err;
    }
    // Facturable de la cuota para el aviso de divergencia de monto (bloque hermano abajo): el importe
    // es del mes; el descuento de acuerdo (OMNET) es a nivel contrato. moneda_dolar → el aviso skipea.
    cuotaParaAviso = cuota
      ? {
          importe: cuota.importe,
          descuento_acuerdo_pct: cGuard.descuento_acuerdo_pct,
          moneda_dolar: cGuard.moneda_dolar,
        }
      : null;
  } else if (vinculacion.venta_tipo === "sales") {
    const { data: sGuard } = await client
      .from("sales")
      .select("edificiosparticipantes, importe, moneda_dolar, descuento_acuerdo_pct")
      .eq("id", vinculacion.venta_ref_id)
      .single();
    salesRow = sGuard;
    if (!tieneEdificiosAsignados(sGuard?.edificiosparticipantes)) {
      const err = new Error(
        "No se puede vincular: la venta no tiene edificios asignados. Asigná los edificios antes de vincular la factura."
      );
      err.code = "EDIFICIOS_FALTANTES";
      throw err;
    }
  }

  // Guardrail de monto (7.22): aviso BLANDO (no bloquea) si el neto del DTE diverge del importe
  // facturable de la venta. Corre ANTES de los writes. Solo para `sales` en moneda local (el
  // facturable de OMNET = importe × 0,75; agencias reales sin descuento → facturable = importe).
  if (vinculacion.venta_tipo === "sales" && salesRow) {
    const { data: emiMonto } = await client.from("facturas_sii_emitidas").select("monto_neto").eq("id", id).single();
    const aviso = avisoDivergenciaMonto(emiMonto?.monto_neto, salesRow);
    if (aviso && typeof window !== "undefined" && typeof window.confirm === "function") {
      const fmt = (n) => Math.round(n).toLocaleString("es-CL");
      const ok = window.confirm(
        `El neto del DTE (${fmt(aviso.dteNeto)}) difiere del importe facturable de la venta ` +
          `(${fmt(aviso.facturable)}) en ${fmt(aviso.diff)}. ¿Vincular de todos modos?`
      );
      if (!ok) {
        const err = new Error("Vinculación cancelada: el neto del DTE no coincide con el facturable de la venta.");
        err.code = "MONTO_DIVERGENTE_CANCELADO";
        throw err;
      }
    }
  }

  // Guardrail de monto (7.22) — hermano del de sales, para contrato-mes (Fase D, cierra el hueco
  // simétrico de Fase 4): aviso BLANDO si el neto del DTE diverge del facturable de la cuota
  // (cuota.importe × (1 − descuento del contrato)). Skip si el contrato es USD (avisoDivergenciaMonto).
  if (vinculacion.venta_tipo === "sales_contratos" && cuotaParaAviso) {
    const { data: emiMonto } = await client.from("facturas_sii_emitidas").select("monto_neto").eq("id", id).single();
    const aviso = avisoDivergenciaMonto(emiMonto?.monto_neto, cuotaParaAviso);
    if (aviso && typeof window !== "undefined" && typeof window.confirm === "function") {
      const fmt = (n) => Math.round(n).toLocaleString("es-CL");
      const ok = window.confirm(
        `El neto del DTE (${fmt(aviso.dteNeto)}) difiere del importe facturable de la cuota ` +
          `(${fmt(aviso.facturable)}) en ${fmt(aviso.diff)}. ¿Vincular de todos modos?`
      );
      if (!ok) {
        const err = new Error("Vinculación cancelada: el neto del DTE no coincide con el facturable de la cuota.");
        err.code = "MONTO_DIVERGENTE_CANCELADO";
        throw err;
      }
    }
  }

  // 1. Insertar vinculación en tabla 1:N
  const { error: vincError } = await client.from("facturas_sii_vinculaciones").upsert(
    {
      factura_sii_emitida_id: id,
      venta_tipo: vinculacion.venta_tipo,
      venta_ref_id: vinculacion.venta_ref_id,
      venta_ref_index: vinculacion.venta_ref_index ?? null,
      vinculado_por: vinculacion.usuario_id,
      vinculado_at: new Date().toISOString(),
    },
    { onConflict: "factura_sii_emitida_id,venta_tipo,venta_ref_id" }
  );
  if (vincError) throw vincError;

  // Snapshot del USD real (fase 1b): congelar monto_usd SOLO para exportación DTE 110 vinculada
  // a una venta `sales` en dólares. En cualquier otro caso (no-110, no-sales, venta CLP) queda
  // null. sales_contratos NO cubierto: deuda explícita para export por contrato.
  let montoUsd = null;
  if (vinculacion.venta_tipo === "sales" && salesRow?.moneda_dolar === true) {
    const { data: emi } = await client.from("facturas_sii_emitidas").select("tipo_dte").eq("id", id).single();
    if (emi?.tipo_dte === 110) montoUsd = salesRow.importe;
  }

  // 2. Marcar factura como vinculada + mantener campos legacy
  const { data, error } = await client
    .from("facturas_sii_emitidas")
    .update({
      estado_vinculacion: "vinculada",
      venta_tipo: vinculacion.venta_tipo,
      venta_ref_id: vinculacion.venta_ref_id,
      venta_ref_index: vinculacion.venta_ref_index || null,
      cliente_id: vinculacion.cliente_id || null,
      monto_usd: montoUsd,
      vinculado_por: vinculacion.usuario_id,
      vinculado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  const factura = data?.[0];

  // Propagar fecha y folio del DTE al concepto vinculado (solo si facturafecha es null)
  if (factura) {
    if (vinculacion.venta_tipo === "sales") {
      await client
        .from("sales")
        .update({
          facturafecha: factura.fecha_emision,
          facturanumero: String(factura.folio),
        })
        .eq("id", vinculacion.venta_ref_id)
        .is("facturafecha", null);
      // A2b (7.22): congelar el % de intermediación al facturar. Aditivo, no aborta (nunca lanza).
      await congelarPctIntermediacion({
        origenTipo: "sale",
        saleId: vinculacion.venta_ref_id,
        facturaDate: factura.fecha_emision,
      });
    }

    if (vinculacion.venta_tipo === "sales_contratos" && vinculacion.venta_ref_index != null) {
      const { data: cData } = await client
        .from("sales_contratos")
        .select("meses")
        .eq("id", vinculacion.venta_ref_id)
        .single();
      if (cData?.meses && Array.isArray(cData.meses)) {
        const idx = vinculacion.venta_ref_index;
        if (idx >= 0 && idx < cData.meses.length && !cData.meses[idx]?.facturafecha) {
          const nuevosMeses = [...cData.meses];
          nuevosMeses[idx] = {
            ...nuevosMeses[idx],
            facturafecha: factura.fecha_emision,
            facturanumero: String(factura.folio),
          };
          await client.from("sales_contratos").update({ meses: nuevosMeses }).eq("id", vinculacion.venta_ref_id);
          // A2b (7.22): congelar el % de intermediación del mes facturado. Aditivo, no aborta.
          await congelarPctIntermediacion({
            origenTipo: "contrato_mes",
            contratoId: vinculacion.venta_ref_id,
            mes: cData.meses[idx].mes,
            facturaDate: factura.fecha_emision,
          });
        }
      }
    }

    // HW-CAPEX (Variante 1): espejo del branch de `sales`. Al vincular un DTE real a un tramo
    // de `detalle_pagos_hardware`, se propaga fecha/folio del DTE AL tramo, SOLO si estaba sin
    // fecha (el tramo "pendiente" nace con facturafecha null — Frente 2). Sin pisar un tramo que
    // ya tuviera una fecha cargada. Hardware no lleva intermediación → no congela % (10.127 n/a).
    if (vinculacion.venta_tipo === "negocios_carteleria_hw" && vinculacion.venta_ref_index != null) {
      const { data: nData } = await client
        .from("negocios_carteleria")
        .select("detalle_pagos_hardware")
        .eq("id", vinculacion.venta_ref_id)
        .single();
      const detalle =
        typeof nData?.detalle_pagos_hardware === "string"
          ? JSON.parse(nData.detalle_pagos_hardware || "[]")
          : nData?.detalle_pagos_hardware || [];
      const idx = vinculacion.venta_ref_index;
      if (Array.isArray(detalle) && idx >= 0 && idx < detalle.length && !detalle[idx]?.facturafecha) {
        const nuevos = [...detalle];
        nuevos[idx] = {
          ...nuevos[idx],
          facturafecha: factura.fecha_emision,
          facturanumero: String(factura.folio),
        };
        await client
          .from("negocios_carteleria")
          .update({ detalle_pagos_hardware: nuevos })
          .eq("id", vinculacion.venta_ref_id);
      }
    }
  }

  return factura;
};

export const desvincularVinculacion = async (facturaId, vinculacion) => {
  // 0. Folio del DTE — necesario para el guard anti-borrado del texto de la venta (paso 4).
  //    Se lee ANTES de revertir SII; la reversión no toca la columna `folio`.
  const { data: dteData } = await client.from("facturas_sii_emitidas").select("folio").eq("id", facturaId).single();
  const folioStr = dteData?.folio != null ? String(dteData.folio) : null;

  // 1. Borrar la vinculación específica
  let query = client
    .from("facturas_sii_vinculaciones")
    .delete()
    .eq("factura_sii_emitida_id", facturaId)
    .eq("venta_tipo", vinculacion.venta_tipo)
    .eq("venta_ref_id", vinculacion.venta_ref_id);
  query =
    vinculacion.venta_ref_index != null
      ? query.eq("venta_ref_index", vinculacion.venta_ref_index)
      : query.is("venta_ref_index", null);
  const { error: delError } = await query;
  if (delError) throw delError;

  // 2. Verificar si quedan otras vinculaciones
  const { data: restantes } = await client
    .from("facturas_sii_vinculaciones")
    .select("id")
    .eq("factura_sii_emitida_id", facturaId);

  // 3. Si no quedan, volver a pendiente
  if (!restantes || restantes.length === 0) {
    await client
      .from("facturas_sii_emitidas")
      .update({
        estado_vinculacion: "pendiente",
        venta_tipo: null,
        venta_ref_id: null,
        venta_ref_index: null,
        cliente_id: null,
        vinculado_por: null,
        vinculado_at: null,
      })
      .eq("id", facturaId);
  }

  // 4. Espejo inverso de vincularFacturaEmitida: limpiar el texto de facturación que el link
  //    propagó a la venta. Solo para los tipos que el link escribe (`sales` / `sales_contratos`)
  //    y SOLO si el número guardado coincide con este folio (no pisar un número de otro flujo).
  //    Va DESPUÉS de revertir SII a propósito: si esto fallara, el lado SII queda bien revertido
  //    (estado no peor que el actual, que de hecho ya dejaba el texto sucio). BTL y carteleria
  //    no se tocan porque el link tampoco les escribe texto.
  if (!folioStr) return;
  if (vinculacion.venta_tipo === "sales") {
    const { data: nulled } = await client
      .from("sales")
      .update({ facturanumero: null, facturafecha: null, plazocobro: null })
      .eq("id", vinculacion.venta_ref_id)
      .eq("facturanumero", folioStr)
      .select("id");
    // A4 (7.22): si se desfacturó (match del folio), invalidar la foto del % → una re-facturación
    // posterior vuelve a congelar al % vigente de ese momento.
    if (nulled?.length) await invalidarPctCongelado({ origenTipo: "sale", saleId: vinculacion.venta_ref_id });
  } else if (vinculacion.venta_tipo === "sales_contratos" && vinculacion.venta_ref_index != null) {
    const { data: cData } = await client
      .from("sales_contratos")
      .select("meses")
      .eq("id", vinculacion.venta_ref_id)
      .single();
    if (cData?.meses && Array.isArray(cData.meses)) {
      const idx = vinculacion.venta_ref_index;
      if (idx >= 0 && idx < cData.meses.length && String(cData.meses[idx]?.facturanumero) === folioStr) {
        const nuevosMeses = [...cData.meses];
        nuevosMeses[idx] = {
          ...nuevosMeses[idx],
          facturanumero: null,
          facturafecha: null,
          plazocobro: null,
        };
        await client.from("sales_contratos").update({ meses: nuevosMeses }).eq("id", vinculacion.venta_ref_id);
        // A4 (7.22): invalidar la foto del % del mes desfacturado (no toca meses hermanos).
        await invalidarPctCongelado({
          origenTipo: "contrato_mes",
          contratoId: vinculacion.venta_ref_id,
          mes: cData.meses[idx].mes,
        });
      }
    }
  } else if (vinculacion.venta_tipo === "negocios_carteleria_hw" && vinculacion.venta_ref_index != null) {
    // Espejo inverso del branch HW-CAPEX de vincularFacturaEmitida: al desvincular, limpiar la
    // fecha/folio que el link propagó al tramo de detalle_pagos_hardware — SOLO si el número
    // guardado coincide con este folio (no pisar un dato de otro flujo). El tramo vuelve a
    // "pendiente" (facturafecha null) → sale de los totales hasta que se le vincule otro DTE.
    const { data: nData } = await client
      .from("negocios_carteleria")
      .select("detalle_pagos_hardware")
      .eq("id", vinculacion.venta_ref_id)
      .single();
    const detalle =
      typeof nData?.detalle_pagos_hardware === "string"
        ? JSON.parse(nData.detalle_pagos_hardware || "[]")
        : nData?.detalle_pagos_hardware || [];
    const idx = vinculacion.venta_ref_index;
    if (
      Array.isArray(detalle) &&
      idx >= 0 &&
      idx < detalle.length &&
      String(detalle[idx]?.facturanumero) === folioStr
    ) {
      const nuevos = [...detalle];
      nuevos[idx] = { ...nuevos[idx], facturanumero: null, facturafecha: null, plazocobro: null };
      await client
        .from("negocios_carteleria")
        .update({ detalle_pagos_hardware: nuevos })
        .eq("id", vinculacion.venta_ref_id);
    }
  }
};

export const getVinculaciones = async (facturaId) => {
  const { data, error } = await client
    .from("facturas_sii_vinculaciones")
    .select("*")
    .eq("factura_sii_emitida_id", facturaId);
  if (error) throw error;
  return data || [];
};

export const ignorarFacturaEmitida = async (id, usuario_id) => {
  const { data, error } = await client
    .from("facturas_sii_emitidas")
    .update({
      estado_vinculacion: "ignorada",
      vinculado_por: usuario_id,
      vinculado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

// ─── Facturas SII Recibidas (Costos) ─────────────────────

export const getFacturasRecibidas = async (pais, filters = {}) => {
  let query = client
    .from("facturas_sii_recibidas")
    .select("*")
    .eq("pais", pais)
    .order("fecha_emision", { ascending: false });

  if (filters.periodo) query = query.eq("periodo_tributario", filters.periodo);
  if (filters.periodos?.length) query = query.in("periodo_tributario", filters.periodos);
  if (filters.estado) query = query.eq("estado_vinculacion", filters.estado);
  if (filters.estado_not) query = query.neq("estado_vinculacion", filters.estado_not);
  if (filters.rut_emisor) query = query.eq("rut_emisor", filters.rut_emisor);
  if (filters.fechaDesde) query = query.gte("fecha_emision", filters.fechaDesde);
  if (filters.fechaHasta) query = query.lte("fecha_emision", filters.fechaHasta);
  if (filters.tipo_dte) query = query.eq("tipo_dte", filters.tipo_dte);
  if (filters.proveedor_id) query = query.eq("proveedor_id", filters.proveedor_id);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getFacturasRecibidasPendientes = async (pais, periodo) =>
  getFacturasRecibidas(pais, { estado: "pendiente", periodo });

// Facturas SII recibidas pagadas vía "pago a edificio": tienen fila en el puente
// movimiento_bancario_facturas (factura_tipo='sii_recibida') colgada de un movimiento bancario
// cuyo movimiento_caja es categoria_pago='comision_edificio'. Su deuda vive en el estado de
// cuenta del edificio (getSaldoEdificio), NO en "Por pagar"; la factura es soporte documental
// (reglas 10.70/10.71). SOLO display: ComprasTab las muestra "Pagada (edificio)" en vez de
// "Pendiente". NO se toca estado_vinculacion (sigue 'pendiente', como manda 10.71).
// Devuelve un Set de factura_id (sii_recibida).
export const getFacturasPagadasViaEdificio = async (pais) => {
  const { data: cajaRows, error: cajaErr } = await client
    .from("movimientos_caja")
    .select("movimiento_bancario_id")
    .eq("pais", pais)
    .eq("categoria_pago", "comision_edificio");
  if (cajaErr) throw cajaErr;
  const mbIds = [...new Set((cajaRows || []).map((r) => r.movimiento_bancario_id).filter((id) => id != null))];
  if (mbIds.length === 0) return new Set();

  const { data: mbfRows, error: mbfErr } = await client
    .from("movimiento_bancario_facturas")
    .select("factura_id")
    .eq("factura_tipo", "sii_recibida")
    .in("movimiento_bancario_id", mbIds);
  if (mbfErr) throw mbfErr;
  return new Set((mbfRows || []).map((r) => r.factura_id).filter((id) => id != null));
};

export const vincularFacturaRecibida = async (id, vinculacion) => {
  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .update({
      estado_vinculacion: "vinculada",
      proveedor_id: vinculacion.proveedor_id || null,
      cuenta_er: vinculacion.cuenta_er,
      cuenta_er_categoria: vinculacion.cuenta_er_categoria || null,
      tipo_contable: vinculacion.tipo_contable || "gasto",
      // Clasificación DELIBERADA per-factura (el usuario asignó y clasificó esta factura) →
      // override manual: ningún automatismo la pisa después.
      clasificacion_manual: true,
      vinculado_por: vinculacion.usuario_id,
      vinculado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

export const ignorarFacturaRecibida = async (id, usuario_id) => {
  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .update({
      estado_vinculacion: "ignorada",
      vinculado_por: usuario_id,
      vinculado_at: new Date().toISOString(),
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

// ─── Reactivar factura ignorada ─────────────────────────

export const reactivarFactura = async (id, tipo) => {
  const tabla = tipo === "emitida" ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
  const { data, error } = await client
    .from(tabla)
    .update({
      estado_vinculacion: "pendiente",
      vinculado_por: null,
      vinculado_at: null,
    })
    .eq("id", id)
    .select();
  if (error) throw error;
  return data?.[0];
};

// ─── Líneas de activo (factura_activo_lineas) ───────────

export const crearLineasActivo = async (lineas) => {
  if (!lineas?.length) return [];
  const { data, error } = await client.from("factura_activo_lineas").insert(lineas).select();
  if (error) throw error;
  return data || [];
};

// ─── Importación (desde Edge Function sii-sync) ──────────

export const insertFacturasEmitidas = async (facturas) => {
  if (!facturas?.length) return { nuevas: 0, existentes: 0 };

  const { data, error } = await client
    .from("facturas_sii_emitidas")
    .upsert(facturas, { onConflict: "tipo_dte,folio", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevas = data?.length || 0;
  return { nuevas, existentes: facturas.length - nuevas };
};

export const insertFacturasRecibidas = async (facturas) => {
  if (!facturas?.length) return { nuevas: 0, existentes: 0 };

  const { data, error } = await client
    .from("facturas_sii_recibidas")
    .upsert(facturas, { onConflict: "rut_emisor,tipo_dte,folio", ignoreDuplicates: true })
    .select();

  if (error) throw error;
  const nuevas = data?.length || 0;
  return { nuevas, existentes: facturas.length - nuevas };
};

// ─── Resumen conciliación ────────────────────────────────

export const getResumenConciliacion = async (pais, periodo) => {
  const [emitidas, recibidas] = await Promise.all([
    getFacturasEmitidas(pais, { periodo }),
    getFacturasRecibidas(pais, { periodo }),
  ]);

  const contarPorEstado = (facturas) => ({
    total: facturas.length,
    pendientes: facturas.filter((f) => f.estado_vinculacion === "pendiente").length,
    vinculadas: facturas.filter((f) => f.estado_vinculacion === "vinculada").length,
    ignoradas: facturas.filter((f) => f.estado_vinculacion === "ignorada").length,
    montoTotal: facturas.reduce((sum, f) => sum + Number(f.monto_total || 0), 0),
  });

  return {
    emitidas: contarPorEstado(emitidas),
    recibidas: contarPorEstado(recibidas),
  };
};
